import { AI_STYLE_QUALITY } from '@/scenes/engine/common/constants'

const OPENAI_MIN_ASPECT_RATIO = 1 / 3
const OPENAI_MAX_ASPECT_RATIO = 3
const OPENAI_DIMENSION_STEP = 16
const OPENAI_MAX_EDGE = 3840
const OPENAI_MIN_PIXELS = 655_360
const OPENAI_MAX_PIXELS = 8_294_400
const OPENAI_TARGET_PIXELS_1K = 1_048_576

type OpenAiOrientationSize = { aspectRatio: number; size: string }
type OpenAiEditMaskAppendOptions = {
  dataUrlToBlobPreservingMime: (dataUrl: string) => Blob
  fileExtensionFromMime: (mimeType: string) => string
}

const OPENAI_TRANSPARENT_SIZES: OpenAiOrientationSize[] = [
  { aspectRatio: 1024 / 1536, size: '1024x1536' },
  { aspectRatio: 1, size: '1024x1024' },
  { aspectRatio: 1536 / 1024, size: '1536x1024' },
]

function sanitizeOpenAiAspectRatio(aspectRatio?: number): number {
  if (!aspectRatio || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return 1
  }

  return Math.min(Math.max(aspectRatio, OPENAI_MIN_ASPECT_RATIO), OPENAI_MAX_ASPECT_RATIO)
}

function fixedTransparentOpenAiSize(aspectRatio: number): string {
  const closest = OPENAI_TRANSPARENT_SIZES.reduce((best, current) => {
    const bestDiff = Math.abs(best.aspectRatio - aspectRatio)
    const currentDiff = Math.abs(current.aspectRatio - aspectRatio)
    return currentDiff < bestDiff ? current : best
  }, OPENAI_TRANSPARENT_SIZES[0])

  return closest.size
}

function constrainedOpenAiSize(aspectRatio: number): string {
  const targetPixels = Math.min(Math.max(OPENAI_TARGET_PIXELS_1K, OPENAI_MIN_PIXELS), OPENAI_MAX_PIXELS)
  const maxStep = Math.floor(OPENAI_MAX_EDGE / OPENAI_DIMENSION_STEP)

  let bestWidth = 1024
  let bestHeight = 1024
  let bestScore = Number.POSITIVE_INFINITY

  for (let widthStep = 1; widthStep <= maxStep; widthStep++) {
    const candidateWidth = widthStep * OPENAI_DIMENSION_STEP
    const idealHeight = candidateWidth / aspectRatio
    const scaledHeight = idealHeight / OPENAI_DIMENSION_STEP
    const heightSteps = new Set([
      Math.floor(scaledHeight),
      Math.round(scaledHeight),
      Math.ceil(scaledHeight),
    ])

    for (const heightStep of heightSteps) {
      if (heightStep <= 0) continue

      const candidateHeight = heightStep * OPENAI_DIMENSION_STEP
      const longestEdge = Math.max(candidateWidth, candidateHeight)
      if (longestEdge > OPENAI_MAX_EDGE) continue

      const pixels = candidateWidth * candidateHeight
      if (pixels < OPENAI_MIN_PIXELS || pixels > OPENAI_MAX_PIXELS) continue

      const candidateAspectRatio = candidateWidth / candidateHeight
      if (candidateAspectRatio < OPENAI_MIN_ASPECT_RATIO || candidateAspectRatio > OPENAI_MAX_ASPECT_RATIO) continue

      const aspectPenalty = Math.abs(Math.log(candidateAspectRatio / aspectRatio))
      const pixelPenalty = Math.abs(pixels - targetPixels) / targetPixels
      const score = aspectPenalty * 10 + pixelPenalty

      if (score < bestScore) {
        bestScore = score
        bestWidth = candidateWidth
        bestHeight = candidateHeight
      }
    }
  }

  return `${bestWidth}x${bestHeight}`
}

export function getOpenAiEditSize(aspectRatio?: number, hasMask?: boolean): string {
  const sanitizedAspectRatio = sanitizeOpenAiAspectRatio(aspectRatio)
  return hasMask ? fixedTransparentOpenAiSize(sanitizedAspectRatio) : constrainedOpenAiSize(sanitizedAspectRatio)
}

export function getOpenAiEditImageQuality(quality?: number): AI_STYLE_QUALITY {
  if (quality === 1) return AI_STYLE_QUALITY.MEDIUM
  if (quality === 2) return AI_STYLE_QUALITY.HIGH
  return AI_STYLE_QUALITY.LOW
}

export function appendOpenAiEditMaskToFormData(
  formData: FormData,
  base64mask: unknown,
  {
    dataUrlToBlobPreservingMime,
    fileExtensionFromMime,
  }: OpenAiEditMaskAppendOptions
): boolean {
  if (!base64mask) {
    return false
  }

  if (typeof base64mask === 'string') {
    const trimmedMask = base64mask.trim()
    if (!trimmedMask) {
      return false
    }

    if (trimmedMask.startsWith('data:')) {
      const maskBlob = dataUrlToBlobPreservingMime(trimmedMask)
      const extension = fileExtensionFromMime(maskBlob.type)
      formData.append('mask', maskBlob, `mask.${extension}`)
      return true
    }

    formData.append('mask', trimmedMask)
    return true
  }

  if (base64mask instanceof Blob) {
    const extension = fileExtensionFromMime(base64mask.type || 'image/png')
    formData.append('mask', base64mask, `mask.${extension}`)
    return true
  }

  const normalizedMask = String(base64mask).trim()
  if (!normalizedMask) {
    return false
  }

  formData.append('mask', normalizedMask)
  return true
}
